import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useJWTAuth } from './auth/JWTAuthContext';

const EntitlementsContext = createContext();

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within EntitlementsProvider');
  }
  return context;
};

export const EntitlementsProvider = ({ children }) => {
  const { publicKey } = useWallet();
  const { login: jwtLogin, isAuthenticated: isJWTAuthenticated, user: jwtUser } = useJWTAuth();
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastLogoutTime, setLastLogoutTime] = useState(null);
  const [userId, setUserId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [debugLogging, setDebugLogging] = useState(false); // Toggle for debug logs

  // Debug logging function
  const debugLog = (...args) => {
    if (debugLogging) {
      console.log(...args);
    }
  };

  // Toggle debug logging (can be called from browser console)
  const toggleDebugLogging = () => {
    setDebugLogging(prev => !prev);
    console.log(`🔧 [Entitlements] Debug logging ${!debugLogging ? 'enabled' : 'disabled'}`);
  };

  // Expose toggle function to window for easy console access
  useEffect(() => {
    window.toggleEntitlementsDebug = toggleDebugLogging;
    return () => {
      delete window.toggleEntitlementsDebug;
    };
  }, [debugLogging]);

  // Fetch user and their entitlements
  const fetchEntitlements = async (walletAddress) => {
    if (!walletAddress) {
      debugLog('👛 [Entitlements] No wallet address provided');
      setEntitlements([]);
      setUserId(null);
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      debugLog('👛 [Entitlements] Fetching user data for wallet:', walletAddress);

      // First, get user by wallet address
      const userResponse = await fetch(`/api/user-lookup/by-wallet/solana/${walletAddress}`);
      
      if (!userResponse.ok) {
        debugLog('❌ [Entitlements] User not found for wallet:', walletAddress);
        setEntitlements([]);
        setUserId(null);
        setPermissions([]);
        setLoading(false);
        return;
      }

      const userData = await userResponse.json();
      debugLog('👤 [Entitlements] User lookup response:', userData);
      
      if (!userData.success || !userData.user) {
        debugLog('❌ [Entitlements] Invalid user data received');
        setEntitlements([]);
        setUserId(null);
        setPermissions([]);
        setLoading(false);
        return;
      }

      const fetchedUserId = userData.user.id;
      debugLog('✅ [Entitlements] Found user ID:', fetchedUserId);
      setUserId(fetchedUserId);

      // Fetch user's entitlements
      debugLog('🔍 [Entitlements] Fetching entitlements for user:', fetchedUserId);
      const entitlementsResponse = await fetch(`/api/entitlements/user/${fetchedUserId}`);
      
      if (entitlementsResponse.ok) {
        const entitlementsData = await entitlementsResponse.json();
        debugLog('📋 [Entitlements] Entitlements data:', entitlementsData);
        if (entitlementsData.success) {
          setEntitlements(entitlementsData.data || []);
          debugLog('✅ [Entitlements] Set entitlements:', entitlementsData.data);
        }
      }

      // Fetch user's permissions from database
      if (fetchedUserId && typeof fetchedUserId === 'number') {
        debugLog('🔐 [Entitlements] Fetching permissions for user:', fetchedUserId);
        try {
          const permissionsResponse = await fetch(`/api/entitlements/permissions/${fetchedUserId}`);

          if (permissionsResponse.ok) {
            const permissionsData = await permissionsResponse.json();
            debugLog('🔐 [Entitlements] Permissions response:', permissionsData);

            if (permissionsData.success && permissionsData.permissions) {
              setPermissions(permissionsData.permissions);
              debugLog('✅ [Entitlements] Set permissions:', permissionsData.permissions);
            } else {
              debugLog('⚠️ [Entitlements] No permissions in response');
              setPermissions([]);
            }
          } else {
            debugLog('⚠️ [Entitlements] Permissions API returned error:', permissionsResponse.status, permissionsResponse.statusText);
            setPermissions([]);
          }
        } catch (err) {
          debugLog('❌ [Entitlements] Could not fetch permissions:', err);
          setPermissions([]);
        }
      } else {
        debugLog('⚠️ [Entitlements] Invalid user ID for permissions fetch:', fetchedUserId);
        setPermissions([]);
      }

    } catch (error) {
      console.error('❌ [Entitlements] Error fetching entitlements:', error);
      setEntitlements([]);
      setUserId(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch entitlements when wallet connects/disconnects
  useEffect(() => {
    const walletAddress = publicKey?.toBase58();

    // If wallet disconnected, clear wallet-based entitlements but preserve JWT auth
    if (!publicKey) {
      debugLog('🔌 Wallet disconnected - clearing wallet-based entitlements (preserving JWT auth)');
      setEntitlements([]);
      // Note: We don't clear userId or permissions here as they may be from JWT auth
      setLoading(false);
    } else {
      // When wallet connects, check if we need to restore JWT authentication
      const attemptAutoJWTLogin = async () => {
        if (!isJWTAuthenticated()) {
          // Don't auto-login if user recently logged out (within 5 seconds)
          const now = Date.now();
          if (lastLogoutTime && (now - lastLogoutTime) < 5000) {
            debugLog('🚫 Skipping auto-login - user recently logged out');
            return;
          }
          
          debugLog('🔄 Wallet connected but no JWT auth - attempting auto-login for:', walletAddress);
          try {
            const result = await jwtLogin(walletAddress);
            if (result.success) {
              debugLog('✅ Auto JWT login successful');
            } else {
              debugLog('❌ Auto JWT login failed:', result.error);
            }
          } catch (error) {
            debugLog('❌ Auto JWT login error:', error);
          }
        }
      };

      // Attempt auto-login first, then fetch entitlements
      attemptAutoJWTLogin().then(() => {
        fetchEntitlements(walletAddress);
      });
    }
  }, [publicKey, isJWTAuthenticated, jwtLogin]);

  // Fetch entitlements from JWT when no wallet is connected but JWT is authenticated
  useEffect(() => {
    const walletAddress = publicKey?.toBase58();
    
    // If we have JWT user but no wallet, fetch entitlements by userId
    if (jwtUser && jwtUser.id && !walletAddress) {
      debugLog('🔐 [Entitlements] JWT auth without wallet - fetching entitlements for userId:', jwtUser.id);
      fetchEntitlementsByUserId(jwtUser.id);
    } else if (!jwtUser && !walletAddress) {
      // No JWT and no wallet - clear entitlements
      debugLog('🔌 [Entitlements] No JWT and no wallet - clearing entitlements');
      setEntitlements([]);
      setUserId(null);
      setPermissions([]);
      setLoading(false);
    }
  }, [jwtUser, publicKey]);

  // Check if user has a specific entitlement
  const hasEntitlement = (entitlementId) => {
    if (!entitlementId) return false;
    return entitlements.some(e => e.entitlement_id === entitlementId);
  };

  // Check if user has admin permissions
  const isAdmin = () => {
    const hasAdminPerms = permissions.includes('admin') || permissions.includes('super_admin') || permissions.includes('moderator');
    debugLog('🔐 [Entitlements] isAdmin() check - userId:', userId, 'permissions:', permissions, 'result:', hasAdminPerms);
    return hasAdminPerms;
  };

  // Refresh entitlements (useful after granting new entitlements)
  const refresh = async () => {
    const walletAddress = publicKey?.toBase58();
    if (walletAddress) {
      await fetchEntitlements(walletAddress);
    } else if (jwtUser && jwtUser.id) {
      await fetchEntitlementsByUserId(jwtUser.id);
    }
  };

  // Set user data from JWT authentication (when wallet is disconnected)
  const setJWTUser = (jwtUserId) => {
    if (jwtUserId && !userId) {
      debugLog('🔐 [Entitlements] Setting user from JWT auth:', jwtUserId);
      setUserId(jwtUserId);
      // Fetch permissions for JWT user
      fetchUserPermissions(jwtUserId);
    }
  };

  // Fetch entitlements by userId (for JWT-only auth, no wallet)
  const fetchEntitlementsByUserId = async (userIdToFetch) => {
    if (!userIdToFetch) {
      debugLog('👛 [Entitlements] No userId provided for fetchEntitlementsByUserId');
      return;
    }

    try {
      setLoading(true);
      debugLog('🔐 [Entitlements] Fetching entitlements for userId (JWT-only):', userIdToFetch);
      setUserId(userIdToFetch);

      // Fetch user's entitlements
      const entitlementsResponse = await fetch(`/api/entitlements/user/${userIdToFetch}`);
      if (entitlementsResponse.ok) {
        const entitlementsData = await entitlementsResponse.json();
        debugLog('📋 [Entitlements] Entitlements data (JWT-only):', entitlementsData);
        if (entitlementsData.success) {
          setEntitlements(entitlementsData.data || []);
          debugLog('✅ [Entitlements] Set entitlements from JWT:', entitlementsData.data);
        }
      }

      // Fetch user's permissions
      await fetchUserPermissions(userIdToFetch);
    } catch (error) {
      console.error('❌ [Entitlements] Error fetching entitlements by userId:', error);
      setEntitlements([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user permissions by user ID (for JWT auth)
  const fetchUserPermissions = async (userIdToFetch) => {
    try {
      debugLog('🔐 [Entitlements] Fetching permissions for user:', userIdToFetch);
      const permissionsResponse = await fetch(`/api/entitlements/permissions/${userIdToFetch}`);
      if (permissionsResponse.ok) {
        const permissionsData = await permissionsResponse.json();
        debugLog('🔐 [Entitlements] Permissions response:', permissionsData);
        if (permissionsData.success && permissionsData.permissions) {
          setPermissions(permissionsData.permissions);
          debugLog('✅ [Entitlements] Set permissions from JWT:', permissionsData.permissions);
        } else {
          debugLog('⚠️ [Entitlements] No permissions in JWT response');
          setPermissions([]);
        }
      } else {
        debugLog('⚠️ [Entitlements] Permissions API returned error:', permissionsResponse.status);
        setPermissions([]);
      }
    } catch (error) {
      debugLog('❌ [Entitlements] Error fetching user permissions:', error);
      setPermissions([]);
    }
  };

  // Function to track when user logs out (to prevent auto-login)
  const trackLogout = () => {
    setLastLogoutTime(Date.now());
    debugLog('🚫 Logout tracked - auto-login disabled for 5 seconds');
  };

  const value = {
    entitlements,
    loading,
    userId,
    hasEntitlement,
    isAdmin,
    refresh,
    setJWTUser,
    trackLogout,
    // Debug controls
    debugLogging,
    setDebugLogging,
    toggleDebugLogging,
    debugLog
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};

